import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Translate } from 'next-translate';

import { useEntitiesStore } from '~/domains/market/state/entities.store';
import { useMarketSettingsStore } from '~/domains/market/state/marketSettings.store';
import { arrayToMap } from '~/common/utils/arrayToMap';
import { settleQuantity } from '~/domains/market/utils/quantity';

import type { PublicPosition } from '../services/interfaces/positions.types';
import type { Position } from '../types/positions.types';

import { transformPositions } from './positions.normalization';

type PositionsState = {
  positionsById: Map<string, PublicPosition>;
  normalizedPositionsById: Map<string, Position>;
  positionsByStatus: {
    open: string[];
    closed: string[];
  };
  activeTab: 'active' | 'final';
  isLoading: boolean;
  error: Error | null;

  setPositions: (
    positions: PublicPosition[],
    normalizedPositions: Position[],
    isFirstPage?: boolean
  ) => void;
  applyPositionSellExecution: (
    positionId: string,
    soldQuantity: number,
    soldProceeds: number,
    t: Translate
  ) => void;
  setActiveTab: (tab: 'active' | 'final') => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  renormalizePositions: (t: Translate) => void;
  reset: () => void;
};

const initialState: Omit<
  PositionsState,
  | 'setPositions'
  | 'applyPositionSellExecution'
  | 'setActiveTab'
  | 'setLoading'
  | 'setError'
  | 'renormalizePositions'
  | 'reset'
> = {
  positionsById: new Map(),
  normalizedPositionsById: new Map(),
  positionsByStatus: {
    open: [],
    closed: [],
  },
  activeTab: 'active',
  isLoading: false,
  error: null,
};

export const usePositionsStore = create<PositionsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setPositions: (positions, normalizedPositions, isFirstPage = true) => {
        const state = get();

        const positionsById = new Map(state.positionsById);
        const normalizedPositionsById = new Map(state.normalizedPositionsById);

        const incomingOpenIds: string[] = [];
        const incomingClosedIds: string[] = [];

        positions.forEach((pos) => {
          positionsById.set(pos.id, pos);
          if (pos.status === 'open') {
            incomingOpenIds.push(pos.id);
          } else {
            incomingClosedIds.push(pos.id);
          }
        });

        normalizedPositions.forEach((pos) => {
          normalizedPositionsById.set(pos.id, pos);
        });

        const mergeIds = (existingIds: string[], incomingIds: string[]): string[] => {
          if (incomingIds.length === 0) {
            return existingIds;
          }

          if (isFirstPage) {
            return incomingIds;
          }

          const existingIdsSet = new Set(existingIds);
          const newIds = incomingIds.filter((id) => !existingIdsSet.has(id));
          return newIds.length > 0 ? [...existingIds, ...newIds] : existingIds;
        };

        const positionsByStatus = {
          open: mergeIds(state.positionsByStatus.open, incomingOpenIds),
          closed: mergeIds(state.positionsByStatus.closed, incomingClosedIds),
        };

        set(
          {
            positionsById,
            normalizedPositionsById,
            positionsByStatus,
            error: null,
          },
          false,
          'setPositions'
        );
      },

      applyPositionSellExecution: (positionId, soldQuantity, soldProceeds, t) => {
        const state = get();
        const currentPosition = state.positionsById.get(positionId);
        if (!currentPosition) return;

        const remainingQuantity = settleQuantity(
          Math.max(currentPosition.quantity - soldQuantity, 0)
        );
        const nextStatus = remainingQuantity === 0 ? 'closed' : 'open';
        const isNowClosed = nextStatus === 'closed';

        const soldFraction =
          currentPosition.quantity > 0
            ? Math.min(soldQuantity / currentPosition.quantity, 1)
            : 0;
        const soldCost = currentPosition.cost * soldFraction;
        const soldCostPlusFees = currentPosition.cost_plus_fees * soldFraction;

        const updatedPosition: PublicPosition = {
          ...currentPosition,
          quantity: remainingQuantity,
          status: nextStatus,
          closed_quantity: currentPosition.closed_quantity + soldQuantity,
          closed_cost: currentPosition.closed_cost + soldCost,
          closed_cost_plus_fees: currentPosition.closed_cost_plus_fees + soldCostPlusFees,
          total_proceeds: (currentPosition.total_proceeds ?? 0) + soldProceeds,
          realized_pnl: currentPosition.realized_pnl + (soldProceeds - soldCostPlusFees),
          order_count: currentPosition.order_count + 1,
          updated_at: Date.now(),
        };

        const positionsById = new Map(state.positionsById);
        positionsById.set(positionId, updatedPosition);

        const entitiesState = useEntitiesStore.getState();
        const priceFormat = useMarketSettingsStore.getState().priceFormat;
        const normalizedPositions = transformPositions(
          [updatedPosition],
          entitiesState.eventsById,
          entitiesState.teamsById,
          entitiesState.entitiesById,
          priceFormat,
          t
        );

        const normalizedPositionsById = new Map(state.normalizedPositionsById);
        const updatedNormalized = normalizedPositions[0];
        if (updatedNormalized) {
          normalizedPositionsById.set(positionId, updatedNormalized);
        }

        const openPositionIds = state.positionsByStatus.open.filter((id) => id !== positionId);
        const closedPositionIds = state.positionsByStatus.closed.filter((id) => id !== positionId);

        const positionsByStatus = {
          open: isNowClosed ? openPositionIds : [positionId, ...openPositionIds],
          closed: isNowClosed ? [positionId, ...closedPositionIds] : closedPositionIds,
        };

        set(
          {
            positionsById,
            normalizedPositionsById,
            positionsByStatus,
          },
          false,
          'applyPositionSellExecution'
        );
      },

      setActiveTab: (tab) =>
        set(
          {
            activeTab: tab,
          },
          false,
          'setActiveTab'
        ),

      setLoading: (loading) =>
        set(
          {
            isLoading: loading,
          },
          false,
          'setLoading'
        ),

      setError: (error) =>
        set(
          {
            error,
          },
          false,
          'setError'
        ),

      renormalizePositions: (t) => {
        const state = get();
        const { positionsById } = state;

        // If no positions, nothing to renormalize
        if (positionsById.size === 0) {
          return;
        }

        // Get current entities and price format
        const entitiesState = useEntitiesStore.getState();
        const priceFormat = useMarketSettingsStore.getState().priceFormat;

        // Convert maps to arrays for transformation
        const positions = Array.from(positionsById.values());
        const events = Array.from(entitiesState.eventsById.values());
        const teams = Array.from(entitiesState.teamsById.values());

        // Transform positions with new price format
        const eventsById = arrayToMap(events);
        const teamsById = arrayToMap(teams);
        const normalizedPositions = transformPositions(
          positions,
          eventsById,
          teamsById,
          entitiesState.entitiesById,
          priceFormat,
          t
        );

        // Update normalized positions
        const normalizedPositionsById = new Map<string, Position>();
        normalizedPositions.forEach((pos) => {
          normalizedPositionsById.set(pos.id, pos);
        });

        set(
          {
            normalizedPositionsById,
          },
          false,
          'renormalizePositions'
        );
      },

      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'PositionsStore' }
  )
);

export function usePositionsActions() {
  const applyPositionSellExecution = usePositionsStore((state) => state.applyPositionSellExecution);
  const setActiveTab = usePositionsStore((state) => state.setActiveTab);
  const setLoading = usePositionsStore((state) => state.setLoading);
  const setError = usePositionsStore((state) => state.setError);
  const renormalizePositions = usePositionsStore((state) => state.renormalizePositions);
  const reset = usePositionsStore((state) => state.reset);

  return {
    applyPositionSellExecution,
    setActiveTab,
    setLoading,
    setError,
    renormalizePositions,
    reset,
  };
}
